import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Mic, MicOff, Loader2, FileText, Clock, UserCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getWSUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}/ws/transcribe/live`;
}

export default function AINotetaker({
  sourceType, // "meeting" or "task"
  sourceId,
  sourceName,
  onNotesCreated
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcriptionResult, setTranscriptionResult] = useState(null);
  const [liveTranscripts, setLiveTranscripts] = useState([]);
  const [livePartialText, setLivePartialText] = useState("");
  const [liveStatus, setLiveStatus] = useState("idle"); // idle | connecting | recording | completed | saving | error

  const recordingTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const liveTranscriptsRef = useRef([]);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const saveNotes = async (transcription) => {
    setLiveStatus("saving");
    try {
      let content = `## AI Notetaker Transcription\n\n`;
      content += `**Source**: ${sourceName}\n`;
      content += `**Date**: ${new Date().toLocaleString()}\n\n`;
      content += `### Transcript\n\n${transcription.text || transcription}`;

      const res = await axios.post(`${API}/comms/notes`, {
        source_type: sourceType,
        source_id: sourceId,
        content: content,
        transcription_id: transcription.id || null,
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      // If meeting, also save to meeting folder for MeetAI
      if (sourceType === "meeting") {
        try {
          await axios.post(`${API}/comms/meetings/${sourceId}/transcription`, {
            transcription: content
          }, {
            headers: { ...getHeaders(), "Content-Type": "application/json" },
            withCredentials: true
          });
          toast.success("Notes saved and added to meeting folder for MeetAI");
        } catch {
          toast.success("Notes saved successfully");
        }
      } else {
        toast.success("Notes saved successfully");
      }

      if (onNotesCreated) {
        onNotesCreated(res.data);
      }
    } catch (err) {
      toast.error("Failed to save notes");
    }
    setLiveStatus("completed");
  };

  const startAudioCapture = (stream, ws, existingAudioContext) => {
    try {
      const audioContext = existingAudioContext || new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const inputData = event.inputBuffer.getChannelData(0);
        const pcm = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        ws.send(pcm.buffer);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
    } catch (err) {
      toast.error("Audio capture setup failed");
      cleanup();
    }
  };

  const cleanup = () => {
    if (processorRef.current && audioContextRef.current) {
      try { processorRef.current.disconnect(); } catch {}
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN ||
            wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close();
        }
      } catch {}
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    setIsRecording(false);
    audioContextRef.current = null;
    processorRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup();
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setLiveStatus("connecting");
      setLiveTranscripts([]);
      setLivePartialText("");
      liveTranscriptsRef.current = [];
      setTranscriptionResult(null);

      // Create AudioContext early to get the actual browser sample rate
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });
      const actualSampleRate = audioContext.sampleRate;
      audioContextRef.current = audioContext;

      const ws = new WebSocket(getWSUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        // Send actual sample rate so backend writes correct WAV headers
        ws.send(JSON.stringify({ type: "config", sample_rate: actualSampleRate }));
        setLiveStatus("recording");
        setIsRecording(true);
        setRecordingTime(0);

        recordingTimerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);

        toast.success("Live transcription started...");
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "transcript") {
            if (msg.message_type === "FinalTranscript" && msg.text.trim()) {
              liveTranscriptsRef.current = [
                ...liveTranscriptsRef.current,
                { text: msg.text, confidence: msg.confidence, final: true },
              ];
              setLiveTranscripts(liveTranscriptsRef.current);
              setLivePartialText("");
            } else if (msg.message_type === "PartialTranscript") {
              setLivePartialText(msg.text);
            }
          } else if (msg.type === "session_started") {
            startAudioCapture(stream, ws, audioContext);
          } else if (msg.type === "completed") {
            const result = {
              transcription: msg.text,
              plain_text: msg.text,
              text: msg.text,
              speakers: 1,
              duration_seconds: recordingTime,
              confidence: 0,
              id: msg.transcription_id,
            };
            setTranscriptionResult(result);
            // Auto-save the notes
            if (sourceId) {
              saveNotes(result);
            } else {
              setLiveStatus("completed");
            }
          } else if (msg.type === "error") {
            toast.error(msg.detail);
            setLiveStatus("error");
          }
        } catch {}
      };

      ws.onerror = () => {
        toast.error("WebSocket connection failed");
        setLiveStatus("error");
        cleanup();
      };

    } catch (err) {
      toast.error("Could not access microphone. Please check permissions.");
      setLiveStatus("idle");
    }
  }, [sourceId, sourceName, onNotesCreated]);

  const stopRecording = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop" }));
    }
    cleanup();
    toast.info("Processing live transcription...");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          data-testid={`notetaker-btn-${sourceId}`}
        >
          <Mic className="w-4 h-4" />
          AI Notetaker
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg flex items-center justify-center">
              <Mic className="w-4 h-4 text-white" />
            </div>
            AI Notetaker
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <p className="text-sm text-slate-600">
            Record audio notes for <span className="font-medium text-slate-800">{sourceName}</span>.
            Transcription streams live as you speak.
          </p>

          {/* Live Transcript Preview */}
          {(isRecording || liveTranscripts.length > 0) && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5">
                {isRecording ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    Live
                  </>
                ) : (
                  <><FileText className="w-3 h-3" /> Transcript</>
                )}
              </div>
              <ScrollArea className="max-h-40 p-3">
                {liveTranscripts.map((t, i) => (
                  <p key={i} className="text-sm text-slate-700 mb-1 last:mb-0">{t.text}</p>
                ))}
                {isRecording && livePartialText && (
                  <p className="text-sm text-slate-400 italic">{livePartialText}</p>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Recording Controls */}
          <div className="bg-slate-50 rounded-xl p-6 text-center">
            {isRecording ? (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Mic className="w-10 h-10 text-red-500" />
                </div>
                <p className="text-2xl font-mono font-bold text-slate-800 mb-2">
                  {formatTime(recordingTime)}
                </p>
                <p className="text-sm text-red-500 mb-4">Recording — transcribing in ~5s chunks...</p>
                <Button
                  onClick={stopRecording}
                  variant="destructive"
                  className="gap-2"
                  data-testid="stop-recording-btn"
                >
                  <MicOff className="w-4 h-4" />
                  Stop & Complete
                </Button>
              </>
            ) : liveStatus === "connecting" ? (
              <>
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                </div>
                <p className="text-sm text-slate-500">Connecting to live transcription...</p>
              </>
            ) : liveStatus === "saving" ? (
              <>
                <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
                </div>
                <p className="text-sm font-medium text-slate-800">Saving notes...</p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4 hover:bg-violet-200 transition-colors cursor-pointer" onClick={startRecording}>
                  <Mic className="w-10 h-10 text-violet-500" />
                </div>
                <p className="text-sm text-slate-600 mb-4">Click to start live recording</p>
                <Button
                  onClick={startRecording}
                  className="gap-2 bg-violet-500 hover:bg-violet-600"
                  data-testid="start-recording-btn"
                >
                  <Mic className="w-4 h-4" />
                  Start Live Recording
                </Button>
              </>
            )}
          </div>

          {/* Completed Transcription Result */}
          {transcriptionResult && liveStatus === "completed" && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Transcription Complete</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {recordingTime}s
                  </span>
                  <span className="flex items-center gap-1">
                    <UserCircle className="w-3 h-3" />
                    Saved
                  </span>
                </div>
              </div>
              <ScrollArea className="max-h-60 p-4">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{transcriptionResult.transcription}</p>
              </ScrollArea>
            </div>
          )}

          <p className="text-xs text-slate-400 text-center">
            Powered by AssemblyAI Real-time Transcription
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
